import { useState } from 'react';
import * as Sentry from '@sentry/browser';

import { PolicyDocumentType } from '@clearcover/core-app-types';

import { useToast } from 'Shared/components/Toast';
import { getPolicyDocumentName } from '../getPolicyDocumentName';
import { useLazyApi } from 'Shared/services/Api';

// this function is IE-specific and has been deprecated and removed from the Navigator type
// We can remove this and the check for it below once we stop supporting IE also
declare global {
  interface Navigator {
    msSaveOrOpenBlob: (blobOrBase64: Blob | string) => void;
  }
}

export const getSignatureDocumentName = (doc: PolicyDocumentType) => {
  const status = doc.completedAt
    ? 'Completed'
    : doc.cancelledAt
    ? 'Cancelled'
    : 'Out For Signature';
  if (doc.signingEvent === 'excluded_driver') {
    return `Excluded Driver Form - ${status}`;
  } else if (doc.signingEvent === 'policy_bind') {
    return `Bind Documents - ${status}`;
  } else {
    return `${doc.signingEvent} - ${status}`;
  }
};

const downloadPdf = (blob: Blob | string, fileName: string) => {
  let data: string;
  if (blob instanceof Blob) {
    const newBlob = new Blob([blob], { type: 'application/pdf' });
    data = window.URL.createObjectURL(newBlob);
    // IE specific
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(newBlob);
      return;
    }
  } else {
    data = blob;
  }
  const link = document.createElement('a');
  link.href = data;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.download = fileName;
  link.click();
};

export const useDocuments = (
  policyId: string,
  documents: Array<PolicyDocumentType>
) => {
  const idCard = documents.find(doc => doc.type === 'id_card');
  const { toast } = useToast();
  const [downloadingDocuments, setDownloadStatus] = useState<{
    [id: string]: null;
  }>({});

  const [, getPolicyDocument] = useLazyApi<ReadableStream>({
    method: 'GET',
  });

  const downloadPolicyDocument = (document: PolicyDocumentType) => async () => {
    const assetId = document.signedAssetId || document.id;
    const url =
      document.signedAssetId !== null
        ? `/policies/${policyId}/signatureDocuments/${assetId}`
        : `/policies/${policyId}/documents/${assetId}`;

    if (!downloadingDocuments.hasOwnProperty(assetId)) {
      setDownloadStatus(oldDocs => ({
        ...oldDocs,
        [assetId]: null,
      }));
      try {
        await getPolicyDocument({
          data: { policyId, documentId: assetId },
          url: url,
          responseType: 'arraybuffer',
        })
          .then(({ data }) => new Response(data))
          .then(response => response.blob())
          .then(blob =>
            downloadPdf(blob, getPolicyDocumentName(document.name))
          );
        setDownloadStatus(
          ({ [assetId]: removeDoc, ...loadingDocs }) => loadingDocs
        );
      } catch (error) {
        if (error instanceof Error) {
          Sentry.captureException(error);
        }
        setDownloadStatus(
          ({ [assetId]: removeDoc, ...loadingDocs }) => loadingDocs
        );
        toast({
          message: `Error Downloading ${getPolicyDocumentName(
            document.name
          )}. Please Try Again.`,
          type: 'error',
          position: 'top',
          withSecondaryNav: false,
          icon: 'error',
        });
      }
    }
  };

  const downloadIdCard = async () => {
    if (idCard) {
      downloadPolicyDocument(idCard)();
    }
  };
  return {
    idCard,
    downloadingDocuments,
    downloadDocument: {
      downloadIdCard,
      downloadPolicyDocument,
    },
  };
};
